import React, { useState } from "react";
import { Box, Typography, Collapse, IconButton } from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from "@mui/icons-material";
import { colors, fonts, spacing } from "../../styles/theme";

/**
 * Nimmt in einem `columns`-Raster alle Spalten der aktuellen Zeile ein – für Felder/Hinweise,
 * die allein stehen sollen statt neben ein Nachbarfeld gesetzt zu werden. Funktioniert
 * unabhängig von der gewählten Spaltenzahl (kein `span 2` fest verdrahtet).
 */
export const subSectionFullWidth = { gridColumn: "1 / -1" } as const;

interface SubSectionProps {
  title: string;
  /** Ersetzt den title-String wenn angegeben (z.B. für editierbare Titel) */
  titleNode?: React.ReactNode;
  children: React.ReactNode;
  defaultExpanded?: boolean;
  /**
   * False macht die Unter-Sektion zu einer reinen Gliederungsüberschrift ohne Klick-/Aufklapp-
   * Verhalten – der Inhalt ist immer sichtbar. `defaultExpanded` wird in dem Fall ignoriert.
   * Für Formulare, bei denen sich Pflichtfelder nicht hinter einem Klick verstecken dürfen.
   */
  expandable?: boolean;
  /**
   * Blendet die Überschrift aus und lässt nur das Raster stehen – für den ersten Abschnitt
   * unter einer Section, deren Titel ihn schon benennt. Nur zusammen mit `expandable={false}`;
   * `title` bleibt Pflicht, damit der Abschnitt im Code benannt ist.
   */
  hideTitle?: boolean;
  totalCost?: string; // Optional: Gesamtkosten rechts anzeigen
  /**
   * Buttons oder andere Aktionen rechts im Header.
   *
   * Bei einer aufklappbaren Unter-Sektion erscheinen sie erst im aufgeklappten Zustand: Sie
   * wirken auf den Inhalt, und ein Button über einem zugeklappten Abschnitt fordert zu einer
   * Aktion auf, deren Ergebnis man nicht sieht.
   */
  actions?: React.ReactNode;
  /**
   * Spaltenzahl für ein Feld-Raster des Inhalts (z.B. 2 für ein zweispaltiges Formular).
   * Ohne Angabe bleibt der Inhalt frei (Tabellen, Listen, sonstiger Aufbau) statt in ein Grid
   * gezwungen zu werden – nur Formulare mit gleich hohen/breiten Feldreihen setzen das.
   * Auf `xs` immer einspaltig, ab `sm` in der angegebenen Spaltenzahl.
   */
  columns?: number;
  /**
   * Karte statt freier Überschrift: feiner Rahmen um Titel und Inhalt, der Titel als kleine
   * Überzeile. Für Formulare in Dialogen, wo große Überschriften im Leerraum die Abschnitte nur
   * lose trennen. Nur zusammen mit `expandable={false}`.
   */
  card?: boolean;
  /** Icon vor dem Titel der Karte – dasselbe wie das Modul in der Sidebar */
  icon?: React.ReactNode;
  sx?: object;
}

/**
 * Unter-Sektion für Detail-Parameter, wahlweise aufklappbar oder als feste Gliederungsüberschrift
 * Wrapper für einheitliches Layout innerhalb von Sections
 *
 * Standard: aufklappbar, zugeklappt (expandable=true, defaultExpanded=false)
 */
const SubSection: React.FC<SubSectionProps> = ({
  title,
  titleNode,
  children,
  defaultExpanded = false,
  expandable = true,
  hideTitle = false,
  totalCost,
  actions,
  columns,
  card = false,
  icon,
  sx = {},
}) => {
  const [expandedState, setExpandedState] = useState(defaultExpanded);
  // Nicht aufklappbar → Inhalt immer sichtbar, unabhängig von defaultExpanded.
  const expanded = expandable ? expandedState : true;

  const handleToggle = () => {
    if (!expandable) return;
    setExpandedState(!expandedState);
  };

  // Row-Gap größer als Column-Gap: trennt Zeilen deutlicher als Felder derselben Zeile,
  // statt beide mit demselben (zu knappen) Wert zu behandeln. Einziger Ort, an dem der
  // Zeilenabstand eines Feld-Rasters definiert wird – einzelne Felder dürfen sich hier nicht
  // per eigenem mb reinreden (siehe InputField), sonst wird das wieder pro Zeile uneinheitlich.
  // `minmax(0, 1fr)` statt `1fr`: ein blankes `1fr` ist `minmax(auto, 1fr)` und lässt eine Spalte
  // über ihren Anteil hinauswachsen, sobald ein Feld breiteren Inhalt hat als die Spalte (etwa
  // eine Antragsart mit langem Paragraphen-Text). Die Spalten wären dann sichtbar ungleich breit.
  const content = columns ? (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "1fr",
          sm: `repeat(${columns}, minmax(0, 1fr))`,
        },
        columnGap: spacing.md,
        rowGap: spacing.xl,
        alignItems: "start",
      }}
    >
      {children}
    </Box>
  ) : (
    children
  );

  if (card) {
    return (
      <Box
        sx={{
          mb: 3,
          p: 3,
          pt: 2.5,
          border: `1px solid ${colors.border.divider}`,
          borderRadius: 2,
          ...sx,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            mb: 3,
            color: colors.text.secondary,
            "& .MuiSvgIcon-root": { fontSize: "1.1rem" },
          }}
        >
          {icon}
          <Typography
            sx={{
              fontSize: "0.75rem",
              fontWeight: fonts.weights.semiBold,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
            }}
          >
            {title}
          </Typography>
        </Box>
        {content}
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 5, ...sx }}>
      {/* Header mit Button links, Titel und optionalen Gesamtkosten */}
      {!(hideTitle && !expandable) && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            cursor: expandable ? "pointer" : "default",
            py: 1,
            pr: 2,
            ...(expandable && {
              "&:hover": {
                backgroundColor: colors.opacity.black04,
              },
            }),
            borderRadius: 1,
          }}
          onClick={handleToggle}
        >
          <Box sx={{ display: "flex", alignItems: "center", flex: 1 }}>
            {expandable && (
              <IconButton
                size="small"
                // Der Pfeil ist der einzige verlässliche Auslöser: ist ein `titleNode` gesetzt,
                // unterbindet dessen Wrapper die Klick-Weitergabe (sonst würde das Klicken in ein
                // Bedienelement im Titel den Abschnitt zuklappen). Ohne Namen wäre er weder für
                // Screenreader noch für Tests ansprechbar.
                aria-label={`${title} ${expanded ? "zuklappen" : "aufklappen"}`}
                sx={{
                  color: expanded ? "primary.main" : "text.secondary",
                  transition: "transform 0.2s ease-in-out",
                  mr: 1,
                }}
              >
                {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            )}
            {titleNode ? (
              <Box sx={{ flex: 1 }} onClick={(e) => e.stopPropagation()}>
                {titleNode}
              </Box>
            ) : (
              <Typography
                variant="h6"
                sx={{
                  fontWeight: fonts.weights.medium,
                  textTransform: "none",
                  fontSize: "1.1rem",
                  color:
                    expandable && expanded ? "primary.main" : "text.primary",
                  flex: 1,
                }}
              >
                {title}
              </Typography>
            )}
            {totalCost && (
              <Typography
                sx={{
                  fontWeight: fonts.weights.semiBold,
                  fontSize: "1rem",
                  color:
                    expandable && expanded ? "primary.main" : "text.secondary",
                  fontFamily: fonts.monospace,
                }}
              >
                {totalCost}
              </Typography>
            )}
            {actions && (!expandable || expanded) && (
              <Box onClick={(e) => e.stopPropagation()}>{actions}</Box>
            )}
          </Box>
        </Box>
      )}

      {/*
        Inhalt bündig zum Rand, nicht zum Titeltext eingerückt: Sonst fluchten Felder in einem
        Abschnitt nicht mit Feldern außerhalb, und genau nebeneinander gestellt fällt der
        Versatz auf.
      */}
      {expandable ? (
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <Box sx={{ pt: 2 }}>{content}</Box>
        </Collapse>
      ) : (
        <Box sx={{ pt: hideTitle ? 0 : 2, pl: "8px" }}>{content}</Box>
      )}
    </Box>
  );
};

export default SubSection;
